import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { 
  FileText, 
  ArrowLeft,
  Loader2,
  AlertCircle,
  Info,
  Edit,
  CheckCircle2,
  XCircle
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Purchase Requisition View Page (Read-Only)
 * Matches the tabbed layout of PREdit/PRCreate but displays data as read-only
 */
const PRView = () => {
  const navigate = useNavigate();
  const { pr_number } = useParams();
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('header');
  const [activeItemIndex, setActiveItemIndex] = useState(0);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  
  const [prData, setPrData] = useState(null);

  useEffect(() => {
    if (pr_number) {
      loadPRData();
    }
  // eslint-disable-next-line
  }, [pr_number]);

  const loadPRData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BACKEND_URL}/api/purchase-requisitions/${pr_number}`);
      const pr = response.data;
      
      setPrData(pr);
      setActiveItemIndex(0);
    } catch (error) {
      console.error('Error loading PR:', error);
      toast.error('Failed to load Purchase Requisition');
      navigate('/p2p/pr-list');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    try {
      setActionLoading(true);
      await axios.post(`${BACKEND_URL}/api/purchase-requisitions/${pr_number}/approve`, null, {
        params: { approved_by: 'APPROVER' }
      });
      toast.success('PR approved successfully!');
      loadPRData();
    } catch (error) {
      console.error('Error approving PR:', error);
      toast.error(error.response?.data?.detail || 'Failed to approve PR');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!rejectionReason.trim()) {
      toast.error('Rejection reason is required');
      return;
    }

    try {
      setActionLoading(true);
      await axios.post(`${BACKEND_URL}/api/purchase-requisitions/${pr_number}/reject`, null, {
        params: { 
          rejected_by: 'APPROVER',
          reason: rejectionReason
        }
      });
      toast.success('PR rejected');
      setShowRejectDialog(false);
      loadPRData();
    } catch (error) {
      console.error('Error rejecting PR:', error);
      toast.error(error.response?.data?.detail || 'Failed to reject PR');
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'OPEN': 'bg-orange-100 text-orange-800 border-orange-200',
      'APPROVED': 'bg-green-100 text-green-800 border-green-200',
      'PARTIALLY_ORDERED': 'bg-yellow-100 text-yellow-800 border-yellow-200',
      'FULLY_ORDERED': 'bg-green-100 text-green-800 border-green-200',
      'REJECTED': 'bg-red-100 text-red-800 border-red-200',
      'CLOSED': 'bg-gray-100 text-gray-800 border-gray-200'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return dateString;
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-screen">
          <Loader2 className="h-8 w-8 animate-spin text-orange-600" />
          <span className="ml-3 text-lg">Loading Purchase Requisition...</span>
        </div>
      </Layout>
    );
  }

  if (!prData || !prData.items || prData.items.length === 0) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Purchase Requisition Not Found</h2>
            <p className="text-gray-600 mb-4">The PR you're looking for doesn't exist or has no items.</p>
            <Button onClick={() => navigate('/p2p/pr-list')}>
              Back to PR List
            </Button>
          </div>
        </div>
      </Layout>
    );
  }

  const currentItem = prData.items[activeItemIndex];

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/p2p/pr-list')}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-2">
                <FileText className="h-6 w-6" />
                View Purchase Requisition
              </h1>
              <p className="text-sm text-gray-500">PR Number: {pr_number}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className={`px-3 py-1.5 rounded-lg text-sm font-semibold border ${getStatusColor(prData.status || 'OPEN')}`}>
              {(prData.status || 'OPEN').replace(/_/g, ' ')}
            </span>
            <Button variant="outline" onClick={() => navigate(`/p2p/pr-edit/${pr_number}`)}>
              <Edit className="h-4 w-4 mr-2" />
              Edit
            </Button>
            {(prData.status === 'OPEN' || !prData.status) && (
              <>
                <Button
                  variant="outline"
                  className="text-red-600 hover:text-red-700 border-red-300"
                  onClick={() => setShowRejectDialog(true)}
                  disabled={actionLoading}
                >
                  <XCircle className="h-4 w-4 mr-2" />
                  Reject
                </Button>
                <Button
                  className="bg-green-600 hover:bg-green-700"
                  onClick={handleApprove}
                  disabled={actionLoading}
                >
                  {actionLoading ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <CheckCircle2 className="h-4 w-4 mr-2" />
                  )}
                  Approve
                </Button>
              </>
            )}
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-5 mb-4">
            <TabsTrigger value="header">PR Header</TabsTrigger>
            <TabsTrigger value="items">Item Overview</TabsTrigger>
            <TabsTrigger value="item-details">Item Details</TabsTrigger>
            <TabsTrigger value="valuation">Valuation</TabsTrigger>
            <TabsTrigger value="account">Account Assignment</TabsTrigger>
          </TabsList>

          {/* ===================== TAB 1: PR HEADER ===================== */}
          <TabsContent value="header">
            <Card>
              <CardHeader>
                <CardTitle>PR Header - General Information</CardTitle>
                <CardDescription>Document control fields (per SAP ER diagram)</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="bg-orange-50 p-4 rounded-md mb-4">
                  <p className="text-sm text-orange-800">
                    <strong>Note:</strong> Per SAP standards, the PR Header contains only Document Type, PR Number, and Source Determination. All other information is specified at the item level.
                  </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Document Type</p>
                    <p className="font-medium text-lg">{prData.document_type || 'NB'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Source Determination</p>
                    <p className="font-medium text-lg">{prData.source_determination ? 'Yes' : 'No'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Created By</p>
                    <p className="font-medium text-lg">{prData.created_by || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Created At</p>
                    <p className="font-medium text-lg">{formatDate(prData.created_at)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Status</p>
                    <p className={`inline-block px-3 py-1 rounded text-sm font-semibold ${getStatusColor(prData.status || 'OPEN')}`}>
                      {(prData.status || 'OPEN').replace(/_/g, ' ')}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Total Items</p>
                    <p className="font-medium text-lg">{prData.items?.length || 0}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ===================== TAB 2: ITEM OVERVIEW ===================== */}
          <TabsContent value="items">
            <Card>
              <CardHeader>
                <CardTitle>PR Line Items</CardTitle>
                <CardDescription>Item overview and basic information</CardDescription>
              </CardHeader>
              <CardContent>
                {/* Item Selector */}
                <div className="flex items-center gap-2 mb-4 flex-wrap">
                  {prData.items.map((item, index) => (
                    <Button
                      key={item.item_number || `pr-item-${index}`}
                      type="button"
                      variant={activeItemIndex === index ? "default" : "outline"}
                      size="sm"
                      onClick={() => setActiveItemIndex(index)}
                    >
                      Item {item.item_number}
                    </Button>
                  ))}
                </div>

                {currentItem && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 border-t pt-4">
                    <div className="md:col-span-3">
                      <div className="bg-orange-50 p-3 rounded-md flex items-start gap-2">
                        <Info className="h-5 w-5 text-orange-600 flex-shrink-0 mt-0.5" />
                        <p className="text-sm text-orange-800">
                          Viewing Item {currentItem.item_number}. Switch tabs to view other sections (Item Details, Valuation, Account Assignment).
                        </p>
                      </div>
                    </div>

                    <div>
                      <p className="text-sm text-gray-600 mb-1">Item Number</p>
                      <p className="font-medium">{currentItem.item_number}</p>
                    </div>

                    <div>
                      <p className="text-sm text-gray-600 mb-1">Item Category</p>
                      <p className="font-medium">{currentItem.item_category || '-'}</p>
                    </div>

                    <div>
                      <p className="text-sm text-gray-600 mb-1">Material</p>
                      <p className="font-medium">{currentItem.material_id || '-'}</p>
                    </div>

                    <div className="md:col-span-2">
                      <p className="text-sm text-gray-600 mb-1">Short Text</p>
                      <p className="font-medium">{currentItem.short_text}</p>
                    </div>

                    <div>
                      <p className="text-sm text-gray-600 mb-1">Material Group</p>
                      <p className="font-medium">{currentItem.material_group || '-'}</p>
                    </div>

                    <div>
                      <p className="text-sm text-gray-600 mb-1">Quantity</p>
                      <p className="font-medium">{currentItem.quantity} {currentItem.unit_of_measure}</p>
                    </div>

                    <div>
                      <p className="text-sm text-gray-600 mb-1">Unit of Measure</p>
                      <p className="font-medium">{currentItem.unit_of_measure}</p>
                    </div>

                    <div>
                      <p className="text-sm text-gray-600 mb-1">Delivery Date</p>
                      <p className="font-medium">{formatDate(currentItem.delivery_date)}</p>
                    </div>

                    <div>
                      <p className="text-sm text-gray-600 mb-1">Delivery Date Category</p>
                      <p className="font-medium">{currentItem.delivery_date_category || 'D'}</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ===================== TAB 3: ITEM DETAILS ===================== */}
          <TabsContent value="item-details">
            <Card>
              <CardHeader>
                <CardTitle>Item {currentItem?.item_number} - Details</CardTitle>
                <CardDescription>Master data, quantities, dates, and procurement references</CardDescription>
              </CardHeader>
              <CardContent>
                {currentItem && (
                  <div className="space-y-6">
                    {/* Master Data Section */}
                    <div>
                      <h3 className="text-md font-semibold mb-3 text-gray-700">Master Data</h3>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <p className="text-sm text-gray-600 mb-1">Plant</p>
                          <p className="font-medium">{currentItem.plant || '-'}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600 mb-1">Storage Location</p>
                          <p className="font-medium">{currentItem.storage_location || '-'}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600 mb-1">Purchasing Organization</p>
                          <p className="font-medium">{currentItem.purchasing_organization || '-'}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600 mb-1">Purchasing Group</p>
                          <p className="font-medium">{currentItem.purchasing_group || '-'}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600 mb-1">Desired Vendor</p>
                          <p className="font-medium">{currentItem.desired_vendor || '-'}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600 mb-1">Fixed Vendor</p>
                          <p className="font-medium">{currentItem.fixed_vendor || '-'}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600 mb-1">Service Number</p>
                          <p className="font-medium">{currentItem.service_number || '-'}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600 mb-1">Valuation Type</p>
                          <p className="font-medium">{currentItem.valuation_type || '-'}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600 mb-1">Batch</p>
                          <p className="font-medium">{currentItem.batch || '-'}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600 mb-1">Agreement Number</p>
                          <p className="font-medium">{currentItem.agreement_number || '-'}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600 mb-1">Info Record Number</p>
                          <p className="font-medium">{currentItem.info_record_number || '-'}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600 mb-1">MRP Controller</p>
                          <p className="font-medium">{currentItem.mrp_controller || '-'}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600 mb-1">Contact Person</p>
                          <p className="font-medium">{currentItem.contact_person || '-'}</p>
                        </div>
                        <div className="md:col-span-2">
                          <p className="text-sm text-gray-600 mb-1">Manufacturer Part Number</p>
                          <p className="font-medium">{currentItem.manufacturer_part_number || '-'}</p>
                        </div>
                      </div>
                    </div>

                    {/* Delivery Address Section */}
                    {(currentItem.title || currentItem.name || currentItem.delivery_street_house_number) && (
                      <div>
                        <h3 className="text-md font-semibold mb-3 text-gray-700">Delivery Address</h3>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div>
                            <p className="text-sm text-gray-600 mb-1">Title</p>
                            <p className="font-medium">{currentItem.title || '-'}</p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-600 mb-1">Name</p>
                            <p className="font-medium">{currentItem.name || '-'}</p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-600 mb-1">Street/House Number</p>
                            <p className="font-medium">{currentItem.delivery_street_house_number || '-'}</p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-600 mb-1">Country</p>
                            <p className="font-medium">{currentItem.delivery_country || '-'}</p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-600 mb-1">Postal Code</p>
                            <p className="font-medium">{currentItem.delivery_postal_code || '-'}</p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Additional Dates Section */}
                    <div>
                      <h3 className="text-md font-semibold mb-3 text-gray-700">Additional Dates</h3>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <p className="text-sm text-gray-600 mb-1">Request Date</p>
                          <p className="font-medium">{formatDate(currentItem.request_date)}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600 mb-1">Release Date</p>
                          <p className="font-medium">{formatDate(currentItem.release_date)}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600 mb-1">Delivery Flexibility</p>
                          <p className="font-medium">{currentItem.delivery_date_flexibility || '0'} days</p>
                        </div>
                      </div>
                    </div>

                    {/* Control Indicators Section */}
                    <div>
                      <h3 className="text-md font-semibold mb-3 text-gray-700">Control Indicators</h3>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <p className="text-sm text-gray-600 mb-1">Goods Receipt Required</p>
                          <p className="font-medium">{currentItem.goods_receipt_required ? 'Yes' : 'No'}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600 mb-1">Invoice Receipt Required</p>
                          <p className="font-medium">{currentItem.invoice_receipt_required ? 'Yes' : 'No'}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600 mb-1">Closed</p>
                          <p className="font-medium">{currentItem.closed ? 'Yes' : 'No'}</p>
                        </div>
                      </div>
                    </div>

                    {/* Item Notes */}
                    {currentItem.notes && (
                      <div>
                        <h3 className="text-md font-semibold mb-2 text-gray-700">Item Notes</h3>
                        <p className="text-gray-700 bg-gray-50 p-3 rounded">{currentItem.notes}</p>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ===================== TAB 4: VALUATION ===================== */}
          <TabsContent value="valuation">
            <Card>
              <CardHeader>
                <CardTitle>Item {currentItem?.item_number} - Valuation</CardTitle>
                <CardDescription>Pricing and valuation information</CardDescription>
              </CardHeader>
              <CardContent>
                {currentItem && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Valuation Price</p>
                      <p className="font-medium">{currentItem.valuation_price ? Number(currentItem.valuation_price).toFixed(2) : '-'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Currency</p>
                      <p className="font-medium">{currentItem.currency || 'USD'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Per Unit (UOM)</p>
                      <p className="font-medium">{currentItem.per_unit_uom || '-'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Estimated Unit Price</p>
                      <p className="font-medium">{currentItem.estimated_unit_price ? Number(currentItem.estimated_unit_price).toFixed(2) : '-'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Price Currency</p>
                      <p className="font-medium">{currentItem.price_currency || 'USD'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Total Value</p>
                      <p className="font-medium text-lg text-green-600">
                        {currentItem.total_value ? `${currentItem.currency || 'USD'} ${Number(currentItem.total_value).toFixed(2)}` : '-'}
                      </p>
                    </div>
                    {currentItem.valuation_price && currentItem.quantity && (
                      <div className="md:col-span-3">
                        <div className="bg-amber-50 p-3 rounded-md">
                          <p className="text-sm text-amber-800">
                            <strong>Calculation:</strong> Total Value = Valuation Price × Quantity = {currentItem.valuation_price} × {currentItem.quantity} = {currentItem.total_value || 0} {currentItem.currency}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ===================== TAB 5: ACCOUNT ASSIGNMENT ===================== */}
          <TabsContent value="account">
            <Card>
              <CardHeader>
                <CardTitle>Item {currentItem?.item_number} - Account Assignment</CardTitle>
                <CardDescription>Cost allocation and account assignment</CardDescription>
              </CardHeader>
              <CardContent>
                {currentItem && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Account Assignment Category</p>
                      <p className="font-medium">{currentItem.account_assignment_category || '-'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 mb-1">G/L Account</p>
                      <p className="font-medium">{currentItem.gl_account || '-'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Cost Center</p>
                      <p className="font-medium">{currentItem.cost_center || '-'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Asset Number</p>
                      <p className="font-medium">{currentItem.asset_number || '-'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Order Number</p>
                      <p className="font-medium">{currentItem.order_number || '-'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Network</p>
                      <p className="font-medium">{currentItem.network || '-'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 mb-1">WBS Element</p>
                      <p className="font-medium">{currentItem.wbs_element || '-'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Profit Center</p>
                      <p className="font-medium">{currentItem.profit_center || '-'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Controlling Area</p>
                      <p className="font-medium">{currentItem.co_area || '-'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Company Code</p>
                      <p className="font-medium">{currentItem.company_code || '-'}</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Reject Dialog */}
      {showRejectDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold mb-4">Reject Purchase Requisition</h3>
            <p className="text-sm text-gray-600 mb-4">
              Please provide a reason for rejecting PR {pr_number}:
            </p>
            <textarea
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              placeholder="Enter rejection reason..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md mb-4"
              rows={4}
              maxLength={200}
            />
            <div className="flex gap-3 justify-end">
              <Button
                variant="outline"
                onClick={() => {
                  setShowRejectDialog(false);
                  setRejectionReason('');
                }}
                disabled={actionLoading}
              >
                Cancel
              </Button>
              <Button
                onClick={handleReject}
                className="bg-red-600 hover:bg-red-700"
                disabled={actionLoading || !rejectionReason.trim()}
              >
                {actionLoading ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <XCircle className="h-4 w-4 mr-2" />
                )}
                Reject PR
              </Button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default PRView;
